import React, { useMemo, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';

import { ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import BatchStatsCards from '~direct-debits/components/payment-batches/BatchStatsCards';
import { columnsFactory, PaymentBatchRow } from '~direct-debits/forms/payment-batches/list';
import { searchFields } from '~direct-debits/forms/payment-batches/search';

import { useGetPaymentBatchesQuery } from '~direct-debits/actions/paymentBatches';

const DEFAULT_LIMIT = 30;
const LOAD_MORE_STEP = 30;

interface ComponentProps {
  isActive?: boolean;
  onSelectBatch: (batchId: string) => void;
}

const PaymentBatchesList: React.FC<ComponentProps> = ({ isActive = true, onSelectBatch }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('dd-payment-batches-search', { limit: DEFAULT_LIMIT });

  const handleSearchSubmit = (values: Record<string, any>) => setParams({ ...values, limit: DEFAULT_LIMIT });

  const { data, isFetching, isError, error } = useGetPaymentBatchesQuery(searchParams, { skip: !isActive });

  // TableView rows must carry an `id` field — payment batch IDs are already
  // unique strings, but expose them on the standard key for clarity.
  const rows = useMemo<PaymentBatchRow[]>(
    () => (data?.rows ?? []).map((row) => ({ ...row }) as PaymentBatchRow),
    [data?.rows],
  );
  const total = data?.total ?? 0;
  const stats = data?.stats;

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || DEFAULT_LIMIT;
    setParams({ ...searchParams, limit: currentLimit + LOAD_MORE_STEP });
  };

  const columns = useMemo(() => columnsFactory({ onSelectBatch }), [onSelectBatch]);

  return (
    <div className="p-2 dd-payment-batches" data-cy="dd-payment-batches-list">
      <BatchStatsCards stats={stats} isLoading={isFetching && !data} />
      <Toolbar
        id="dd-payment-batches"
        rightCluster={
          <>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="dd-payment-batches-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle search bar"
            />
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <TableView
          id="dd-payment-batches-table"
          columns={columns}
          data={rows}
          isLoading={isFetching}
          emptyMessage="No payment batches found"
          readonly
          striped
          allowPagination
          totalCount={total}
          onLoadMore={handleLoadMore}
          showCount
          pageSize={Number(searchParams.limit) || DEFAULT_LIMIT}
        />
      )}
    </div>
  );
};

export default PaymentBatchesList;
